import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { updateTask, type Task } from '../lib/api'

/**
 * Task edits, applied optimistically.
 *
 * Every task edit in the app is a small, high-frequency gesture — tick a
 * checkbox, drop a card in another column, pick a date — and each one used to
 * wait on a round-trip before anything moved. So the cache is patched first and
 * the request is reconciled behind it; a failure rolls the snapshot back.
 *
 * `['tasks', …filters]` is one query per filter combination and the same task
 * appears in several of them, so the patch goes through `setQueriesData` on the
 * `['tasks']` prefix rather than one key.
 */

type TaskPatch = Partial<
  Pick<Task, 'status' | 'due' | 'title' | 'description' | 'priority' | 'discipline'>
>

/** Applies `patch` to a task everywhere it is currently cached. */
function patchCaches(qc: QueryClient, id: string, patch: TaskPatch) {
  qc.setQueriesData<{ items: Task[] }>({ queryKey: ['tasks'] }, (old) =>
    old?.items
      ? { ...old, items: old.items.map((t) => (t.id === id ? { ...t, ...patch } : t)) }
      : old,
  )
  qc.setQueryData<Task>(['task', id], (old) => (old ? { ...old, ...patch } : old))
}

export function useUpdateTask() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TaskPatch }) => updateTask(id, patch),

    onMutate: async ({ id, patch }) => {
      // Stop in-flight refetches from landing on top of the optimistic patch.
      await qc.cancelQueries({ queryKey: ['tasks'] })
      await qc.cancelQueries({ queryKey: ['task', id] })

      const snapshot = {
        lists: qc.getQueriesData<{ items: Task[] }>({ queryKey: ['tasks'] }),
        detail: qc.getQueryData<Task>(['task', id]),
      }
      patchCaches(qc, id, patch)
      return snapshot
    },

    onError: (_err, { id }, snapshot) => {
      if (!snapshot) return
      for (const [key, data] of snapshot.lists) qc.setQueryData(key, data)
      qc.setQueryData(['task', id], snapshot.detail)
    },

    // Settled, not success: a rolled-back failure also needs the truth from the
    // server, since the optimistic patch may have raced a real change.
    onSettled: (_data, _err, { id }) => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['task', id] })
    },
  })
}
